import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useTimerControls } from '../../hooks/useTimer';
import { useTimer } from '@/app/store/timer.store';
import { useTaskDetail } from '@/features/tasks/queries';
import { Icon } from '@/shared/ui/atoms/Icon';
import { Mono } from '@/shared/ui/atoms/Mono';
import { formatClock, formatMinutes } from '@/shared/utils/time';
import './TimerCard.styl';

/**
 * The live timer (after the chosen "score" reference): a light glass card with the clock large in the centre, a
 * live/paused pill, the task in a chip, three glass tiles (estimate, logged, left) and a round action button.
 * Reads the single timer store, so it always matches the task cards and the modal.
 */
export function TimerCard() {
  const { snapshot, elapsed, running, lastTaskTitle, lastTaskId } = useTimer();
  const { start, stop, starting, stopping } = useTimerControls();
  const navigate = useNavigate();
  const { t } = useTranslation(undefined, { keyPrefix: 'timer' });
  const taskId = snapshot?.taskId ?? lastTaskId;
  const detail = useTaskDetail(taskId);
  const title = snapshot?.taskTitle ?? lastTaskTitle;
  const task = detail.data?.task;
  const estimate = task?.estimateMinutes ?? 0;
  const logged = task?.loggedMinutes ?? 0;
  const share = estimate > 0 ? Math.min(100, Math.round((logged / estimate) * 100)) : 0;
  const busy = starting || stopping;
  const action = running ? t('pause') : taskId ? t('start') : t('pick');

  const onToggle = () => {
    if (running) void stop();
    else if (taskId) void start(taskId);
    else navigate('/board');
  };

  return (
    <section className={`f-timer${running ? ' f-timer--live' : ''}`} aria-label={t('title')}>
      <span className="f-timer__aura" aria-hidden="true" />
      <span className="f-timer__shape" aria-hidden="true" />
      <div className="f-timer__head">
        <span className="f-timer__title">{t('title')}</span>
        <span className="f-timer__status">
          <span className="f-timer__status-dot" aria-hidden="true" />
          {running ? t('live') : t('paused')}
        </span>
      </div>

      <Mono className="f-timer__clock" aria-live="off" data-testid="timer-clock">
        {formatClock(elapsed)}
      </Mono>
      <div className="f-timer__chip" title={title ?? undefined}>
        {title ?? t('none')}
      </div>

      <div className="f-timer__tiles">
        <Tile label={t('estimateLabel')} value={task ? formatMinutes(estimate) : '—'} percent={task && estimate > 0 ? 100 : 0} />
        <Tile label={t('loggedLabel')} value={task ? formatMinutes(logged) : '—'} percent={share} />
        <Tile label={t('leftLabel')} value={task && estimate > 0 ? formatMinutes(Math.max(0, estimate - logged)) : '—'} percent={task && estimate > 0 ? 100 - share : 0} />
      </div>

      <div className="f-timer__action">
        <span className="f-timer__action-text">{running ? t('runningHint') : taskId ? t('resumeHint') : t('pickHint')}</span>
        <button type="button" className="f-timer__go" onClick={onToggle} disabled={busy} aria-label={action} title={action}>
          <Icon name={running ? 'pause' : taskId ? 'play' : 'chevronRight'} size={16} strokeWidth={2} />
        </button>
      </div>
    </section>
  );
}

/** A glass tile: label, value and a thin bar for its share of the estimate. */
function Tile({ label, value, percent }: { label: string; value: string; percent: number }) {
  return (
    <div className="f-timer__tile">
      <span className="f-timer__bar" aria-hidden="true">
        <span className="f-timer__bar-fill" style={{ width: `${percent}%` }} />
      </span>
      <Mono className="f-timer__value">{value}</Mono>
      <span className="f-timer__label">{label}</span>
    </div>
  );
}
